import { useCallback } from 'react'

import { VideoStreamEdit } from '@nativewaves/nw-cloud-sdk/content'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { useAudioStreamUpdateMutation, useVideoStreamUpdateMutation } from 'hooks/mutations/content'
import { audioStreamQueryKeys, videoStreamQueryKeys } from 'services/queryKeys'

export type StreamKind = 'video' | 'audio'

/** `AudioStreamEdit` is field-identical to `VideoStreamEdit`, so one type covers both. */
export type StreamEdit = VideoStreamEdit

type SaveOptions = {
  onSuccess?: () => void
  onError?: () => void
}

export type UseStreamDetailProps = {
  streamKind: StreamKind
  streamId: string
  /** Pass `false` to suspend polling — e.g. while an edit form is open. */
  refetchInterval?: number | false
}

/**
 * Video and audio streams are field-identical but live on separate endpoints. This hides that
 * split behind one interface so the General tab can be written once.
 */
export const useStreamDetail = ({ streamKind, streamId, refetchInterval }: UseStreamDetailProps) => {
  const queryClient = useQueryClient()

  const isVideo = streamKind === 'video'

  const videoStreamQuery = useQuery({
    ...videoStreamQueryKeys.detail({ streamId }),
    enabled: isVideo,
    refetchInterval,
  })
  const audioStreamQuery = useQuery({
    ...audioStreamQueryKeys.detail({ streamId }),
    enabled: !isVideo,
    refetchInterval,
  })
  const streamQuery = isVideo ? videoStreamQuery : audioStreamQuery

  const videoStreamUpdateMutation = useVideoStreamUpdateMutation()
  const audioStreamUpdateMutation = useAudioStreamUpdateMutation()
  const updateMutation = isVideo ? videoStreamUpdateMutation : audioStreamUpdateMutation

  const save = useCallback(
    (edit: StreamEdit, options?: SaveOptions) => {
      if (isVideo) {
        videoStreamUpdateMutation.mutate({ streamId, videoStreamEdit: edit }, options)
        return
      }
      audioStreamUpdateMutation.mutate({ streamId, audioStreamEdit: edit }, options)
    },
    [isVideo, streamId, videoStreamUpdateMutation, audioStreamUpdateMutation],
  )

  const refresh = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: isVideo
          ? videoStreamQueryKeys.detail({ streamId }).queryKey
          : audioStreamQueryKeys.detail({ streamId }).queryKey,
      }),
    [queryClient, isVideo, streamId],
  )

  return {
    stream: streamQuery.data,
    isLoading: streamQuery.isLoading,
    isError: streamQuery.isError,
    refresh,
    save,
    isSaving: updateMutation.isPending,
  }
}
